import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { TenantFields, EMPTY_TENANT, todayYMD } from "@/components/TenantFields";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";

const freshForm = () => ({ ...EMPTY_TENANT, move_in_date: todayYMD() });

export const MoveInDialog = ({ sites, restrictedKey, onDone, triggerClassName = "h-9 gap-2 border-green-300 text-green-700 hover:bg-green-50", triggerLabel = "Move In Tenant" }) => {
  const [open, setOpen] = useState(false);
  const [siteFilter, setSiteFilter] = useState(restrictedKey || "ALL");
  const [units, setUnits] = useState([]);
  const [unitId, setUnitId] = useState("");
  const [form, setForm] = useState(freshForm);
  const [reserve, setReserve] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setForm(freshForm());
    setReserve(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    api.getUnits({ status: "vacant", site: siteFilter }).then((l) => { setUnits(l); setUnitId((c) => (l.find((u) => u.id === c) ? c : (l[0]?.id || ""))); }).catch(() => setUnits([]));
  }, [open, siteFilter]);

  const submit = async () => {
    if (!unitId) { toast.error("Select a vacant shed"); return; }
    if (!form.first_name.trim() && !form.surname.trim()) { toast.error("Enter the tenant name"); return; }
    if (reserve && !form.move_in_date) { toast.error("Pick the move-in date to reserve until"); return; }
    setBusy(true);
    try {
      await api.updateUnit(unitId, { status: reserve ? "reserved" : "occupied", ...form, reserved_until: reserve ? form.move_in_date : "" });
      toast.success(reserve ? "Shed reserved" : "Tenant moved in");
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to move in tenant"); } finally { setBusy(false); }
  };

  const siteOpts = (restrictedKey ? [] : [{ key: "ALL", label: "All sites" }]).concat((sites || []).map((s) => ({ key: s.key, label: s.name })));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="movein-tenant-btn" variant="outline" className={triggerClassName}><UserPlus className="h-4 w-4" /> {triggerLabel}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-auto" data-testid="movein-dialog">
        <DialogHeader><DialogTitle>Move In a New Tenant</DialogTitle><DialogDescription>Pick a vacant shed and enter the tenant's details. Reserve to hold it until the move-in date without occupying it yet.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Site</Label><Select value={siteFilter} onValueChange={setSiteFilter}><SelectTrigger data-testid="movein-site"><SelectValue /></SelectTrigger><SelectContent>{siteOpts.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Vacant Shed</Label><Select value={unitId} onValueChange={setUnitId}><SelectTrigger data-testid="movein-unit"><SelectValue placeholder={units.length ? "Select shed" : "None available"} /></SelectTrigger><SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.unit_number} · {u.size || "—"} · {u.site_name}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <TenantFields form={form} set={set} prefix="movein" />
          <label className="flex items-center gap-2 cursor-pointer"><Checkbox data-testid="movein-reserve" checked={reserve} onCheckedChange={(v) => setReserve(!!v)} /><span className="text-sm text-slate-700">Reserve until the move-in date (hold without occupying)</span></label>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="movein-submit" onClick={submit} disabled={busy || !unitId} className="bg-green-600 hover:bg-green-700">{busy ? "Saving..." : (reserve ? "Reserve Shed" : "Move In")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
